export interface Tpa {}

export interface ListAccountIdsRequest {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
}

export interface ListAccountIdsResponse {
  /** Account IDs. */
  accountIds?: string[];
}

export interface BusinessStatusRequest {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
}

export interface BusinessStatusResponse {
  /** List of statue. */
  status?: BusinessStatusWithLiterals;
  /** The reason for the error in free text. */
  failureReasonMessage?: string | null;
}

/** Business Status. */
export enum BusinessStatus {
  NONE = 'NONE',
  READY = 'READY',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  NOT_STARTED = 'NOT_STARTED',
}

/** @enumType */
export type BusinessStatusWithLiterals =
  | BusinessStatus
  | 'NONE'
  | 'READY'
  | 'PENDING'
  | 'ERROR'
  | 'NOT_STARTED';

export interface EstimateDeliveryRequest
  extends EstimateDeliveryRequestDispatchTimeOneOf {
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
  /** Delivery pickup address. */
  pickupAddress?: Address;
  /** Delivery dropoff address. */
  dropoffAddress?: Address;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
}

/** @oneof */
export interface EstimateDeliveryRequestDispatchTimeOneOf {
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
}

/** Physical address */
export interface Address {
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /** Coordinates of the physical address. */
  geocode?: AddressLocation;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

export interface EstimateDeliveryResponse {
  /** Optional. Used as reference to the estimate when creating a new order. */
  id?: string | null;
  /** Amount that is charged for this delivery. */
  fee?: Money;
  /** Estimated pickup time. */
  promisedPickupTime?: Date | null;
  /** Estimated dropoff time. */
  promisedDropoffTime?: Date | null;
}

export interface CreateDeliveryRequest
  extends CreateDeliveryRequestDispatchTimeOneOf {
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The name of the business where the order should be picked up from. */
  pickupBusinessName: string;
  /** Estimate ID. */
  estimateId?: string | null;
  /** Delivery pickup address. */
  pickupAddress: Address;
  /** Delivery dropoff address. */
  dropoffAddress: Address;
  /** Phone number of the store where the order should be picked up from. */
  pickupPhoneNumber: string;
  /** Customer details. */
  customer: Customer;
  /** Instructions for the courier at the pickup location. */
  pickupInstructions?: string | null;
  /** Instructions for the courier at the dropoff location. */
  dropoffInstructions?: string | null;
  /** Is contactless delivery. */
  contactlessDelivery?: boolean | null;
  /** Order ID. */
  orderId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
  /** Amount that will be paid to the courier. */
  orderTip: Money;
}

/** @oneof */
export interface CreateDeliveryRequestDispatchTimeOneOf {
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
}

export interface Customer {
  /** First name. */
  firstName?: string;
  /** Last name. */
  lastName?: string;
  /** Phone number. */
  phoneNumber?: string;
  /**
   * Email address.
   * @format EMAIL
   */
  email?: string;
}

export interface CreateDeliveryResponse {
  /** The vendor new order id */
  id?: string;
  /** When a delivery is ready to be picked up. This is the start of the pickup window. */
  pickupWindowStartTime?: Date | null;
  /** When a delivery must be picked up by. This is the end of the pickup window. */
  pickupWindowEndTime?: Date | null;
  /** When a delivery is ready to be dropped off. This is the start of the dropoff window. */
  dropoffWindowStartTime?: Date | null;
  /** When a delivery must be dropped off by. This is the end of the dropoff window. */
  dropoffWindowEndTime?: Date | null;
  /** Amount that is charged for this delivery. */
  fee?: Money;
}

/** @docsIgnore */
export type EstimateDeliveryApplicationErrors = {
  code?: 'ESTIMATE_DELIVERY_REJECTED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CreateDeliveryApplicationErrors = {
  code?: 'CREATE_DELIVERY_REJECTED';
  description?: string;
  data?: Record<string, any>;
};
